/**
 * Corpses (IDEAS X33 stage 1b; owner, Oct 10: "after player or bot dies body drops, then despawns on some timer, maybe
 * new bot will spawn in before body disappears"). A death makes a corpse record of its own — a respawn reuses the
 * unit's record, so the body and the respawned unit cannot both be drawn from it — which lies until its time runs out
 * or the per-battle cap pushes it out (oldest first), then fades over CORPSE_FADE_SECONDS and is gone. Presentation
 * only: the records are made from the deaths the match already reports and nothing in the match reads them. Pure;
 * times are match seconds, so a pause holds the bodies and a replay ages them alike.
 */

export type CorpseFade = 'sink' | 'fade';
export type DeathCause = 'blast' | 'bullet' | 'fall' | 'other';

export interface CorpseConfig {
  /** Seconds a body lies before it starts to go (owner: "not sure what ideal config is" — try 60). */
  seconds: number;
  /** Bodies a battle keeps at most; past it the oldest go first (try 24). */
  cap: number;
  /** How a body goes: sinks into the ground, or fades out. */
  fade: CorpseFade;
}

export const CORPSE_DEFAULTS: CorpseConfig = { seconds: 60, cap: 24, fade: 'sink' };
/** A body takes this long to sink or fade once it starts. */
export const CORPSE_FADE_SECONDS = 2;

export interface Corpse {
  id: number;
  unitId: number;
  team: number;
  cause: DeathCause;
  /** match seconds at the death */
  diedAt: number;
  /** match seconds it began to go (its time ran out, or the cap pushed it), once it has */
  goingFrom?: number;
}

export class CorpseStore {
  private nextId = 1;
  readonly corpses: Corpse[] = [];

  constructor(public config: CorpseConfig = CORPSE_DEFAULTS) {}

  /** A death at `t`: a new body. Past the cap, the oldest still lying begin to go at once. */
  add(unitId: number, team: number, cause: DeathCause, t: number): Corpse {
    const c: Corpse = { id: this.nextId++, unitId, team, cause, diedAt: t };
    this.corpses.push(c);
    const lying = this.corpses.filter(x => x.goingFrom === undefined);
    for (let i = 0; i < lying.length - this.config.cap; i++) lying[i].goingFrom = t;
    return c;
  }

  /**
   * At `t`: bodies whose time ran out begin to go; those gone are removed and returned; the rest report how far
   * they have gone (0 lying … 1 gone).
   */
  tick(t: number): { gone: Corpse[]; state: Array<{ corpse: Corpse; going: number }> } {
    const gone: Corpse[] = [], state: Array<{ corpse: Corpse; going: number }> = [];
    for (let i = 0; i < this.corpses.length; i++) {
      const c = this.corpses[i];
      if (c.goingFrom === undefined && t >= c.diedAt + this.config.seconds) c.goingFrom = c.diedAt + this.config.seconds;
      const going = c.goingFrom === undefined ? 0 : Math.min(1, Math.max(0, (t - c.goingFrom) / CORPSE_FADE_SECONDS));
      if (going >= 1) { gone.push(c); this.corpses.splice(i--, 1); }
      else state.push({ corpse: c, going });
    }
    return { gone, state };
  }

  clear(): Corpse[] { return this.corpses.splice(0); }
}

let override: CorpseConfig | null = null;

/** The Lab's launch sets the corpse config (`null` returns to the page's URL / the defaults). */
export function setCorpseConfig(c: CorpseConfig | null): void { override = c; }

/** The config in force: the Lab's, else the page's `?corpsetime=&corpsecap=&corpsefade=`, else the defaults. */
export function corpseConfig(): CorpseConfig {
  if (override) return override;
  try {
    const q = typeof location !== 'undefined' ? new URLSearchParams(location.search) : null;
    if (!q) return CORPSE_DEFAULTS;
    const num = (k: string, d: number, lo: number, hi: number) => { const v = Number(q.get(k)); return q.has(k) && Number.isFinite(v) ? Math.min(hi, Math.max(lo, v)) : d; };
    const fade = q.get('corpsefade');
    return {
      seconds: num('corpsetime', CORPSE_DEFAULTS.seconds, 0, 3600),
      cap: Math.round(num('corpsecap', CORPSE_DEFAULTS.cap, 0, 200)),
      fade: fade === 'fade' || fade === 'sink' ? fade : CORPSE_DEFAULTS.fade,
    };
  } catch { return CORPSE_DEFAULTS; }
}
